<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.driver.tourList.newTour.buttonText')"
      @click="newTourModalOpen = true"
    />
    <export
      url="/tours/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <new-tour-modal
    v-if="modelValue"
    v-model:open="newTourModalOpen"
    :driver="modelValue"
    @saved="refetch()"
  />
  <edit-tour-modal
    v-if="modelValue"
    v-model:open="editTourModalOpen"
    :driver="modelValue"
    :tour="editTour"
    @saved="refetch()"
  />

  <div class="main-area h-full">
    <div class="ml-12 mr-12">
      <ControlledTourMap
        ref="map"
        v-if="modelValue"
        :auto-center="true"
        :lazy="false"
        :disabled="driverNeedsLoading"
        :filter="totalFilter"
        :use-bounds="false"
        :has-embedded-date-range="true"
        :date-range-restriction="{
          start: halfYearAgo,
          end: new Date(),
        }"
        show-last-known-position
      />
    </div>

    <div class="ml-12">
      <div class="text-body-regular-bold text-table-text-gray uppercase mb-5" data-v-67ba7ed1="">
        {{ $t('pages.driver.tourList.tableHeader') }}
      </div>

      <div class="table">
        <data-table
          class="mb-auto"
          v-model:sort-by="sortKey"
          v-model:sort-direction="sortDirection"
          :busy="loading"
          :items="records"
          :columns="columns"
          :actions="actions"
          :visible-columns="visibleColumns"
        />
      </div>
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import {computed, reactive, ref, unref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Action from '@components/utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg?component';
import NewTourModal from '@pages/drivers/driver/NewTourModal.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, {type Column} from '@utils/table/Table.vue';
import {
  collOptionsProvider,
  type Filter as ProviderFilter,
  ProviderParams,
  useProvider,
} from '@utils/table/hooks/provider';
import {addMonths, format} from 'date-fns';
import type {Filter} from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import type {Driver, HydraCollection, Region, Tour} from '@/entities/Entities';
import {Campaign} from '@/entities/Entities';
import {formatTimeDurationFromSeconds} from '@/utils/helpers';
import Export from '@utils/toolbar/panes/Export.vue';
import ControlledTourMap from '@/components/utils/maps/ControlledTourMap.vue';
import {useQueryRef} from '@/hooks/url';
import {useDelete} from '@hooks/api';
import EditTourModal from '@pages/drivers/driver/EditTourModal.vue';
import {useTableFields} from '@hooks/table-configuration';

interface Props {
  modelValue: Driver | null;
}

const props = defineProps<Props>();

const newTourModalOpen = ref(false);
const editTourModalOpen = ref(false);

const editTour = ref();

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef<ProviderFilter>('filter', {});
const fixedFilter = computed(() => ({ driver: props.modelValue?.id ?? '' }));

const halfYearAgo = ref(addMonths(new Date(), -6));

const map = ref();
watch(
  () => map.value,
  () => {
    const gisLocation = props.modelValue?.region?.gisLocation;
    map.value?.moveToCenter(gisLocation?.lat, gisLocation?.long);
  }
);

const totalFilter = computed(() => {
  return { ...fixedFilter.value, ...filter.value };
});

const driverNeedsLoading = computed(() => {
  return props.modelValue == null;
});

const sortKey = useQueryRef<string | undefined>('sortKey', 'createdAt');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const { t } = useI18n();

const csvTitle = computed(() => {
  return t('csv.title.toursFromDriver', {
    driver: props.modelValue?.user?.fullName,
  });
});

const [deleteTour, { loading: deleting }] = useDelete();

const actions = [
  {
    title: t('pages.driver.tourList.actions.edit'),
    action: (item: Tour) => {
      editTour.value = item;
      editTourModalOpen.value = true;
    },
  },
  {
    title: t('pages.driver.tourList.actions.delete'),
    action: async (item: Tour) => {
      if (await deleteTour(`/tours/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        refetch();
      }
    },
  },
];

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.driver.tourList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    visible: false,
    sortable: false,
    filter: {
      type: 'text',
      group: t('pages.driver.tourList.filterGroups.general'),
      options: {
        label: t('id'),
      },
    },
  },
  {
    key: 'createdAt',
    title: t('tour.createdAt'),
    group: t('pages.driver.tourList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => format(new Date(v), 'dd.MM.yyyy - HH:mm:ss'),
    filter: {
      type: 'datetime',
      group: t('pages.driver.tourList.filterGroups.general'),
      options: {
        label: t('tour.createdAt'),
      },
    },
  },
  {
    key: 'uploadedAt',
    title: t('uploadedAt'),
    group: t('pages.driver.tourList.fieldGroups.general'),
    sortable: true,
    visible: false,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-',
    filter: {
      type: 'datetime',
      group: t('pages.driver.tourList.filterGroups.general'),
      options: {
        label: t('uploadedAt'),
      },
    },
  },
  {
    key: 'driver.region',
    title: t('tour.region'),
    group: t('pages.driver.tourList.fieldGroups.general'),
    formatter: (v: Region) => v.name,
    sortable: true,
    visible: true,
    filter: {
      type: 'dropdown',
      group: t('pages.driver.tourList.filterGroups.general'),
      options: {
        searchable: true,
        options: collOptionsProvider<Region>('/regions', {
          sort: {
            sortKey: 'name',
            sortDirection: 'ASC',
          },
        }, (value) => ({
          label: value.name,
          value: value.id,
        })),
        label: t('tour.region'),
      },
    },
  },
  {
    key: 'campaignRegistration.campaign.group.name',
    title: t('tour.campaign'),
    group: t('pages.driver.tourList.fieldGroups.general'),
    visible: true,
    filter: {
      key: 'campaignRegistration.campaign.group',
      type: 'dropdown',
      group: t('pages.driver.tourList.filterGroups.general'),
      options: {
        searchable: true,
        minChars: 2,
        filterResults: false,
        clearOnSelect: true,
        delay: 500,
        createOption: false,
        resolveOnLoad: true,
        noOptionsText: t('inputSearchString'),
        options: (query: string, select: any) => {
          const input = query || select.modelValue;
          if (!input) return Promise.resolve([]);
          const filterQuery: ProviderParams = {
            filter: query ? { name: input } : { id: select.modelValue },
          };
          return collOptionsProvider<Campaign>('/campaign_groups', filterQuery, (value) => ({
            label: value.name,
            value: value.id,
          }))();
        },
        label: t('tour.campaign'),
      },
    },
  },
  {
    key: 'licenseSeatRegistration.licenseSeat.license',
    title: t('licenses.license'),
    group: t('pages.driver.tourList.fieldGroups.general'),
    sortable: false,
    visible: true,
    computed: (tour: Tour) =>
      tour.licenseSeatRegistration?.licenseSeat?.license ?
      `${tour.licenseSeatRegistration?.licenseSeat?.license?.company?.name} - ${tour.licenseSeatRegistration?.licenseSeat?.license?.startDate ? format(new Date(tour.licenseSeatRegistration?.licenseSeat?.license?.startDate), 'dd.MM.yyyy') : ''} - ${tour.licenseSeatRegistration?.licenseSeat?.license?.endDate ? format(new Date(tour.licenseSeatRegistration?.licenseSeat?.license?.endDate), 'dd.MM.yyyy') : ''}`
      : '-'
  },
  {
    key: 'exists[licenseSeatRegistration]',
    title: t('pages.tours.filters.forBusiness'),
    group: t('pages.tours.fieldGroups.general'),
    sortable: false,
    visible: false,
    computed: (item: Tour) => (item.licenseSeatRegistration !== null) ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.tours.fieldGroups.general'),
      options: {
        label: t('pages.tours.filters.forBusiness'),
      },
    },
  },
  {
    key: 'exists[campaignRegistration]',
    title: t('pages.tours.filters.campaign'),
    group: t('pages.tours.fieldGroups.general'),
    sortable: false,
    visible: false,
    computed: (item: Tour) => (item.campaignRegistration !== null) ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.tours.fieldGroups.general'),
      options: {
        label: t('pages.tours.filters.campaign'),
      },
    },
  },
  {
    key: 'distance',
    title: t('tour.distance'),
    group: t('pages.driver.tourList.fieldGroups.general'),
    sortable: true,
    visible: true,
    unit: 'km',
    formatter: (v: number) => (v / 1000).toFixed(2) + ' km',
    filter: {
      type: 'range',
      group: t('pages.driver.tourList.filterGroups.general'),
      options: {
        icon: <span>km</span>,
        label: t('tour.distance'),
        transformOut: (v: number) => v * 1000,
        transformIn: (v: number) => v / 1000,
      },
    },
  },
  {
    key: 'time',
    title: t('tour.time'),
    group: t('pages.driver.tourList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: number) => formatTimeDurationFromSeconds(v),
    filter: {
      type: 'duration',
      group: t('pages.driver.tourList.filterGroups.general'),
      options: {
        placeholderFrom: 'hh:mm:ss',
        placeholderTo: 'hh:mm:ss',
        label: t('tour.time'),
      },
    },
  },
  {
    key: 'averageSpeed',
    title: t('tour.speed'),
    group: t('pages.driver.tourList.fieldGroups.calculations'),
    sortable: true,
    visible: true,
    unit: 'km/h',
    formatter: (v: number) => (v * 3.6).toFixed(2) + ' km/h',
    filter: {
      type: 'range',
      group: t('pages.driver.tourList.filterGroups.calculations'),
      options: {
        icon: <span>km/h</span>,
        label: t('tour.speed'),
        step: 'any',
        min: '0',
        transformOut: (v: number) => v / 3.6,
        transformIn: (v: number) => v * 3.6,
      },
    },
  },
  {
    key: 'kcal',
    title: t('tour.kcal'),
    group: t('pages.driver.tourList.fieldGroups.calculations'),
    sortable: true,
    visible: true,
    unit: 'kcal',
    filter: {
      type: 'range',
      group: t('pages.driver.tourList.filterGroups.calculations'),
      options: {
        icon: <span>kcal</span>,
        label: t('tour.kcal'),
      },
    },
  },
  {
    key: 'co2',
    title: t('tour.co2'),
    group: t('pages.driver.tourList.fieldGroups.calculations'),
    sortable: true,
    visible: true,
    unit: 'kg',
    filter: {
      type: 'range',
      group: t('pages.driver.tourList.filterGroups.calculations'),
      options: {
        icon: <span>t</span>,
        label: t('tour.co2'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const {
  data: items,
  loading,
  refetch,
} = useProvider<HydraCollection<Tour>>('/tours', providerParams, {
  disabled: driverNeedsLoading,
});

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
